import React from 'react';

/**
 * Editable requirement levels (1-10) for a job's must-have / good-to-have skills.
 *
 * Shared by the campus-job form and the Mantrika review modal so both let a TPO
 * override the generated numbers in the same way. `source` tells the TPO where a
 * value came from: `vendor` (Mantrika's own number), `ai` (generated), `tpo`
 * (edited here) or `stored` (saved previously, not asserted).
 */
const SkillScoreEditor = ({
  mustNames = [],
  goodNames = [],
  scores = { must: {}, good: {} },
  onChange,
  onGenerate,
  generating = false,
  readOnly = false,
  canGenerate = true,
  title = 'Requirement Scores (1–10)',
  helpText = 'Generated from the job title and description, and used as the “Job Requirement” levels in each student’s Skill Gap graph. Edit any value to override it.',
  testId = 'skill-score-editor',
}) => {
  const groups = [
    { key: 'must', label: 'Must-Have', tone: '#16a34a', names: mustNames },
    { key: 'good', label: 'Good-to-Have', tone: '#0ea5e9', names: goodNames },
  ];

  const total = groups.reduce((sum, group) => sum + group.names.length, 0);
  const scored = groups.reduce(
    (sum, group) => sum + group.names.filter((name) => {
      const entry = scores?.[group.key]?.[String(name).trim().toLowerCase()];
      return entry && entry.score !== null && entry.score !== undefined;
    }).length,
    0
  );

  const sourceBadge = (entry) => {
    if (!entry) return { text: '—', color: '#94a3b8', background: '#f1f5f9' };
    if (entry.source === 'tpo') return { text: 'Edited', color: '#b45309', background: '#fef3c7' };
    if (entry.source === 'vendor') return { text: 'Vendor', color: '#475569', background: '#e2e8f0' };
    if (entry.source === 'ai') return { text: 'AI', color: '#4338ca', background: '#e0e7ff' };
    return entry.score !== null && entry.score !== undefined
      ? { text: 'Saved', color: '#0369a1', background: '#e0f2fe' }
      : { text: '—', color: '#94a3b8', background: '#f1f5f9' };
  };

  return (
    <div data-testid={testId}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>{title}</span>
            <span
              style={{
                fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px',
                color: scored === total ? '#047857' : '#b45309',
                background: scored === total ? '#d1fae5' : '#fef3c7',
                padding: '2px 8px', borderRadius: '999px',
              }}
            >
              {scored}/{total} scored
            </span>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: '12px', lineHeight: 1.5, color: '#64748b', maxWidth: '560px' }}>
            {helpText}
          </p>
        </div>
        {!readOnly && onGenerate && (
          <button
            type="button"
            onClick={() => onGenerate()}
            disabled={generating || !canGenerate}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 13px',
              borderRadius: '9px', border: '1px solid #c7d2fe', background: '#eef2ff',
              color: '#3730a3', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap',
              cursor: generating || !canGenerate ? 'not-allowed' : 'pointer',
              opacity: generating || !canGenerate ? 0.6 : 1,
            }}
          >
            {generating ? 'Scoring…' : '✨ Generate scores'}
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '14px', marginTop: '12px' }}>
        {groups.map((group) => (
          <div key={group.key} style={{ minWidth: 0, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px 14px 6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: group.tone }}>
                {group.label}
              </span>
              <span style={{ fontSize: '10.5px', fontWeight: 600, color: '#94a3b8' }}>
                {group.names.length} {group.names.length === 1 ? 'skill' : 'skills'}
              </span>
            </div>

            {group.names.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#94a3b8', padding: '10px 0 14px' }}>No skills on this job.</div>
            ) : group.names.map((name) => {
              const entry = scores?.[group.key]?.[String(name).trim().toLowerCase()];
              const value = entry?.score;
              const invalid = value !== null && value !== undefined && (value < 1 || value > 10);
              const badge = sourceBadge(entry);
              return (
                <div
                  key={`${group.key}-${name}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderTop: '1px solid #f1f5f9' }}
                >
                  <span
                    style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={name}
                  >
                    {name}
                  </span>
                  <span
                    style={{
                      flexShrink: 0, fontSize: '10px', fontWeight: 800, letterSpacing: '0.2px', color: badge.color,
                      background: badge.background, padding: '2px 7px', borderRadius: '999px', minWidth: '44px', textAlign: 'center',
                    }}
                  >
                    {badge.text}
                  </span>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    step="0.5"
                    placeholder="1–10"
                    value={value ?? ''}
                    onChange={(e) => onChange && onChange(group.key, name, e.target.value)}
                    disabled={readOnly}
                    aria-label={`${group.label} requirement score for ${name}`}
                    aria-invalid={invalid ? 'true' : 'false'}
                    style={{
                      width: '74px', height: '34px', minWidth: '74px', flexShrink: 0, textAlign: 'center', boxSizing: 'border-box',
                      fontSize: '13px', fontWeight: 700, color: '#0f172a',
                      border: invalid
                        ? '1.5px solid #ef4444'
                        : value === null || value === undefined
                          ? '1.5px solid #fcd34d'
                          : '1px solid #cbd5e1',
                      borderRadius: '8px',
                      background: readOnly ? '#f8fafc' : '#ffffff',
                      outline: 'none',
                    }}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SkillScoreEditor;
